import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import {
  Briefcase,
  Clock,
  GraduationCap,
  Star,
  Building2,
  Calendar,
  Eye,
  Users,
  CheckCircle,
  TrendingUp,
  X,
  AlertCircle,
  Loader2,
  Upload,
  RefreshCw,
} from "lucide-react";
import parse from "html-react-parser";
import {
  getPublicJobBySlug,
  checkApplicationStatus,
  applyForJob,
  previewJobMatch,
} from "../api/client";
import api from "../api/client";
import MetaTags from "./MetaTags";
import { usePageLoading } from "../contexts/LoadingContext";
import "../components/admin/admin.css";
import logo from "../assets/AptiviewLogo.png";
import ResponsiveLayout from "./Sidebar/components/ResponsiveLayout";
import B2CInterviewSetupModal from "./modals/B2CInterviewSetupModal";

const getNormalizedCredits = (data) => {
  const payload = data?.results || data || {};
  const primaryCredits = [
    payload.available_credits,
    payload.availableCredits,
    payload.remaining_credits,
    payload.remainingCredits,
    payload.balance,
  ]
    .map((value) => Number(value))
    .find((value) => Number.isFinite(value));

  if (Number.isFinite(primaryCredits)) {
    return primaryCredits;
  }

  const currentBalance = Number(payload.current_balance ?? payload.currentBalance);
  const dailyCredits = Number(payload.daily_credits ?? payload.dailyCredits);

  return (Number.isFinite(currentBalance) ? currentBalance : 0) +
    (Number.isFinite(dailyCredits) ? dailyCredits : 0);
};

export default function PublicJobPage() {
  const { year, month, day, slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [jobData, setJobData] = useState(null);
  const [loading, setLoading] = useState(true);
  usePageLoading(loading, { message: t('common.loading', 'Loading…') });
  const [error, setError] = useState(null);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [skillMatchData, setSkillMatchData] = useState(null);
  const [skillMatchLoading, setSkillMatchLoading] = useState(false);
  const [showSkillMatchDialog, setShowSkillMatchDialog] = useState(false);
  const [showInterviewPrompt, setShowInterviewPrompt] = useState(false);
  const [interviewPromptLoading, setInterviewPromptLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [userResumes, setUserResumes] = useState([]);
  const [resumesLoading, setResumesLoading] = useState(false);
  const [coverLetterInput, setCoverLetterInput] = useState("");
  const [showResumeSelectDialog, setShowResumeSelectDialog] = useState(false);
  const [selectedResume, setSelectedResume] = useState("");
  const [showPracticeModal, setShowPracticeModal] = useState(false);
  const [credits, setCredits] = useState(null);
  const [selectedResumeData, setSelectedResumeData] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    setIsAuthenticated(!!token);
  }, []);

  useEffect(() => {
    if (showResumeSelectDialog) {
      fetchUserResumes();
    }
  }, [showResumeSelectDialog]);

  useEffect(() => {
    if (userResumes?.length > 0) {
      const activeResume = userResumes.find((resume) => resume.is_active);
      if (activeResume) {
        setSelectedResume(activeResume.id);
      } else {
        setSelectedResume(""); // or first resume if needed
      }
    }
  }, [userResumes]);


  useEffect(() => {
    const fetchJobData = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await getPublicJobBySlug(year, month, day, slug);
        setJobData(data);

        document.title = `${data.job.title} - ${data.job.company_name}`;

        const metaDescription = document.querySelector(
          'meta[name="description"]'
        );
        if (metaDescription) {
          const description = data.job.description?.substring(0, 150) + "...";
          metaDescription.setAttribute("content", description);
        }
      } catch (err) {
        console.error("Error fetching job:", err);
        if (err.response?.status === 404) {
          setError(t('publicJobPage.jobNotFound'));
        } else {
          setError(t('publicJobPage.failedToLoad'));
        }
      } finally {
        setLoading(false);
      }
    };

    if (year && month && day && slug) {
      fetchJobData();
    }
  }, [year, month, day, slug]);

  useEffect(() => {
    const checkStatus = async () => {
      if (jobData?.job?.id) {
        const token = localStorage.getItem("token");
        if (token) {
          try {
            const status = await checkApplicationStatus(jobData.job.id);
            setApplicationStatus(status);
          } catch (err) {
            console.error("Error checking application status:", err);
          }
        }
      }
    };

    checkStatus();
  }, [jobData?.job?.id]);

  const fetchUserResumes = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      setResumesLoading(true);
      const response = await api.get("/resumes/", {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      const resumes = response.data.results || response.data || [];
      setUserResumes(resumes);
      if (resumes.length > 0) {
        setSelectedResume(resumes[0].id);
      }
    } catch (err) {
      console.error("Error fetching resumes:", err);
    } finally {
      setResumesLoading(false);
    }
  };

  const uploadResume = async (file) => {
    try {
      setUploadLoading(true);

      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post("/resumes/", formData, {
        headers: {
          Authorization: `Token ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
        },
      });

      setUserResumes((prev) => [response.data, ...prev]);

      setSnackbar({
        open: true,
        message: t('publicJobPage.resumeUploadSuccess'),
        severity: "success",
      });

      setShowUploadDialog(false);

      setTimeout(async () => {
        if (jobData?.job?.id) {
          try {
            const status = await checkApplicationStatus(jobData.job.id);
            setApplicationStatus(status);
          } catch (err) {
            console.error("Error rechecking application status:", err);
          }
        }
      }, 1000);
    } catch (err) {
      console.error("Failed to upload resume:", err);
      setSnackbar({
        open: true,
        message:
          err.response?.data?.error ||
          t('publicJobPage.failedToUploadResume'),
        severity: "error",
      });
    } finally {
      setUploadLoading(false);
    }
  };

  const handleResumeUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    if (!allowedTypes.includes(file.type)) {
      setSnackbar({
        open: true,
        message: t('publicJobPage.pleaseUploadPDFOrWord'),
        severity: "error",
      });
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setSnackbar({
        open: true,
        message: t('publicJobPage.fileSizeMustBeLessThan10MB'),
        severity: "error",
      });
      return;
    }

    await uploadResume(file);
    event.target.value = "";
  };

  const formatSalary = (min, max) => {
    if (min && max) {
      return `₹${Number(min).toLocaleString()} - ₹${Number(
        max
      ).toLocaleString()}`;
    } else if (min) {
      return t('publicJobPage.salaryFrom', { amount: Number(min).toLocaleString() });
    } else if (max) {
      return t('publicJobPage.salaryUpTo', { amount: Number(max).toLocaleString() });
    }
    return t('publicJobPage.salaryNotSpecified');
  };

  const formatExperienceLevel = (level) => {
    if (!level) return t('publicJobPage.notSpecified');
    return level.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const formatWorkType = (type) => {
    if (!type) return t('publicJobPage.notSpecified');
    return type.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const formatEmploymentType = (type) => {
    if (!type) return t('publicJobPage.notSpecified');
    return type.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const checkUserApplicationStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return {
          canApply: false,
          reason: t('publicJobPage.pleaseLoginToApply'),
        };
      }

      const status = await checkApplicationStatus(jobData.job.id);
      return status;
    } catch (err) {
      console.error("Error checking application status:", err);
      if (err.response?.status === 401) {
        return {
          canApply: false,
          reason: t('publicJobPage.pleaseLoginToApply'),
        };
      }
      return { canApply: false, reason: t('publicJobPage.errorCheckingStatus') };
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("role");
    setIsAuthenticated(false);
    navigate("/login");
  };

  const handleApplyClick = async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      const currentUrl = window.location.pathname;
      navigate("/login", {
        state: {
          redirectTo: currentUrl,
          jobTitle: jobData?.job?.title,
          companyName: jobData?.job?.company_name,
        },
      });
      return;
    }

    const status = await checkUserApplicationStatus();

    if (status.has_applied) {
      setSnackbar({
        open: true,
        message: t('publicJobPage.alreadyAppliedStatus', { status: status.application.status_display }),
        severity: "info",
      });
      return;
    }

    if (!status.can_apply) {
      if (status.reason && status.reason.toLowerCase().includes("resume")) {
        setShowUploadDialog(true);
        setUserResumes([]);
        return;
      }

      setSnackbar({
        open: true,
        message: status.reason || t('publicJobPage.unableToApply'),
        severity: "error",
      });
      return;
    }

    setShowResumeSelectDialog(true);
  };

  const stripHtml = (html) => {
    if (!html) return "";
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.body.textContent || "";
    } catch {
      return html.replace(/<[^>]*>/g, '');
    }
  };

  const handlePracticeClick = async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      const currentUrl = window.location.pathname + "?practice=true";
      navigate("/login", {
        state: {
          redirectTo: currentUrl,
          jobTitle: jobData?.job?.title,
          companyName: jobData?.job?.company_name,
        },
      });
      return;
    }

    let resumes = userResumes;
    if (resumes.length === 0) {
      try {
        setResumesLoading(true);
        const response = await api.get("/resumes/", {
          headers: {
            Authorization: `Token ${token}`,
          },
        });
        resumes = response.data.results || response.data || [];
        setUserResumes(resumes);
      } catch (err) {
        console.error("Error fetching resumes for practice:", err);
      } finally {
        setResumesLoading(false);
      }
    }

    const activeResume = resumes.find((r) => r.is_active) || resumes[0];
    if (!activeResume) {
      setShowUploadDialog(true);
      return;
    }

    try {
      const creditResponse = await api.get("observability/user-credits/");
      const fetchedCredits = getNormalizedCredits(creditResponse.data);
      setCredits(fetchedCredits);
    } catch (err) {
      console.error("Error fetching credits for practice:", err);
    }

    setSelectedResumeData(activeResume);
    setShowPracticeModal(true);
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const params = new URLSearchParams(window.location.search);
    if (params.get("practice") === "true" && token && jobData?.job) {
      // Remove query param from URL
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
      // Automatically trigger the practice interview modal
      handlePracticeClick();
    }
  }, [jobData]);

  const handleResumeSelectionAndPreview = async () => {
    try {
      setSkillMatchLoading(true);
      setShowResumeSelectDialog(false);
      const matchData = await previewJobMatch(jobData.job.id, selectedResume);
      setSkillMatchData(matchData);
      setShowSkillMatchDialog(true);
    } catch (err) {
      console.error("Error getting skill match:", err);
      setSnackbar({
        open: true,
        message: t('publicJobPage.unableToAnalyze'),
        severity: "error",
      });
    } finally {
      setSkillMatchLoading(false);
    }
  };

  const handleConfirmApply = async () => {
    try {
      setApplying(true);
      setConfirmDialogOpen(false);

      if (userResumes.length === 0) {
        setSnackbar({
          open: true,
          message:
            t('publicJobPage.needResumeBeforeApply'),
          severity: "warning",
        });
        setShowUploadDialog(true);
        await fetchUserResumes();
        return;
      }

      const userCover = (coverLetterInput || "").trim();
      const finalCoverLetter =
        userCover.length > 0
          ? userCover
          : t('publicJobPage.defaultCoverLetter', { title: jobData.job.title, company: jobData.job.company_name });

      const application = await applyForJob({
        job_id: jobData.job.id,
        resume_id: selectedResume,
        cover_letter: finalCoverLetter,
        expected_salary: null,
        earliest_start_date: null,
      });

      setApplicationStatus({ has_applied: true, application });
      setShowInterviewPrompt(true);
    } catch (err) {
      console.error("Error applying for job:", err);
      setSnackbar({
        open: true,
        message:
          err.response?.data?.error ||
          t('publicJobPage.failedToSubmitApplication'),
        severity: "error",
      });
    } finally {
      setApplying(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleProceedWithApplication = () => {
    setShowSkillMatchDialog(false);
    setCoverLetterInput("");
    setConfirmDialogOpen(true);
  };

  const handleCancelApplication = () => {
    setShowSkillMatchDialog(false);
    setSkillMatchData(null);
  };

  const handleInterviewPromptResponse = async (wantsInterview) => {
    if (!wantsInterview) {
      setShowInterviewPrompt(false);
      setSnackbar({
        open: true,
        message:
          t('publicJobPage.applicationSuccessNoInterview'),
        severity: "success",
      });
      return;
    }

    try {
      setInterviewPromptLoading(true);

      const creditResponse = await api.get("observability/user-credits/");
      const credits = getNormalizedCredits(creditResponse.data);

      if (credits > 0) {
        const inviteResponse = await api.post(
          `job-applications/${applicationStatus.application.id}/create_interview_session/`
        );
        const inviteUrl = inviteResponse.data.invite_url.startsWith("http")
          ? inviteResponse.data.invite_url
          : `${window.location.origin}${inviteResponse.data.invite_url}`;

        const generatedLinks = JSON.parse(
          localStorage.getItem("generatedInterviewLinks") || "{}"
        );
        generatedLinks[applicationStatus.application.id] = {
          inviteUrl: inviteUrl,
          jobTitle: jobData.job.title,
          companyName: jobData.job.company_name,
          createdAt: new Date().toISOString(),
        };
        localStorage.setItem(
          "generatedInterviewLinks",
          JSON.stringify(generatedLinks)
        );

        setShowInterviewPrompt(false);
        setSnackbar({
          open: true,
          message: t('publicJobPage.interviewLinkGenerated', { url: inviteUrl }),
          severity: "success",
        });
      } else {
        setShowInterviewPrompt(false);
        setSnackbar({
          open: true,
          message:
            t('publicJobPage.noCredits'),
          severity: "warning",
          autoHideDuration: 8000,
        });
      }
    } catch (err) {
      console.error("Error handling interview prompt:", err);

      let errorMessage =
        t('publicJobPage.unableToProcessInterview');
      let isCreditsError = false;

      if (err.response?.status === 402) {
        const creditsRemaining = err.response?.data?.credits_remaining || 0;
        errorMessage =
          err.response?.data?.error ||
          t('publicJobPage.insufficientCredits');
        errorMessage += ' ' + t('publicJobPage.creditsRemaining', { count: creditsRemaining });
        isCreditsError = true;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
        isCreditsError = errorMessage.toLowerCase().includes("credit");
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
        autoHideDuration: isCreditsError ? 8000 : 6000,
      });
    } finally {
      setInterviewPromptLoading(false);
    }
  };

  if (loading) return null;

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-4">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
              <p className="text-red-800">{error}</p>
            </div>
          </div>
          <button
            onClick={() => window.location.href = "https://www.aptiview.ai/job-board"}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl transition-colors"
          >
            {t('publicJobPage.goToHome')}
          </button>
        </div>
      </div>
    );
  }

  if (!jobData) {
    return null;
  }

  const { job } = jobData;

  const pageTitle = `${job.title} - ${job.company_name}`;
  const pageDescription = job.description?.substring(0, 150) + "...";
  const pageUrl = `${window.location.origin}/jobs/${year}/${month}/${day}/${slug}`;
  const pageKeywords = [
    job.title,
    job.company_name,
    job.department_name,
    formatExperienceLevel(job.experience_level),
    formatWorkType(job.work_type),
    ...(job.required_skills || []),
  ]
    .filter(Boolean)
    .join(", ");

  // Job Details Content Component
  const JobDetailsContent = () => (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Main Job Content */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-3xl lg:text-4xl text-gray-900 mb-3">
                {job.title}
              </h1>

              <div className="flex items-center space-x-4 mb-4">
                <div className="flex items-center space-x-3">
                  {/* <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-blue-600" />
                  </div> */}

                  <div className="flex items-center justify-center border border-gray-200 rounded-lg px-2 py-1">
                    {job.company_logo ? (
                      <img
                        src={job.company_logo}
                        alt={`${job.company_name} logo`}
                        className="h-14 max-w-[80px] object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <Building2 className="h-8 w-8 text-blue-600" />
                    )}
                  </div>


                  <div>
                    <h2 className="text-xl text-blue-600">
                      {job.company_name}
                    </h2>
                    <p className="text-gray-600">{job.department_name}</p>
                  </div>
                </div>
              </div>

              {/* Job Stats */}
              <div className="flex items-center space-x-6 text-gray-500 mb-4">
                <div className="flex items-center space-x-1">
                  <Eye className="h-4 w-4" />
                  <span>{job.view_count} {t('publicJobPage.views')}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Users className="h-4 w-4" />
                  <span>{job.application_count} {t('publicJobPage.applications')}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Calendar className="h-4 w-4" />
                  <span><span className="text-blue-600 font-medium">{formatDate(job.updated_at)}</span></span>
                </div>
              </div>

              {/* Job Type Chips */}
              <div className="flex flex-wrap gap-2">
                {job.is_premium && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-yellow-100 text-yellow-800">
                    <Star className="h-3 w-3 mr-1" />
                    {t('publicJobPage.premium')}
                  </span>
                )}
                {job.is_featured && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-purple-100 text-purple-800">
                    {t('publicJobPage.featured')}
                  </span>
                )}
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800 border border-blue-200">
                  {formatEmploymentType(job.employment_type)}
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-800 border border-green-200">
                  {formatWorkType(job.work_type)}
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-pink-100 text-pink-800 border border-pink-200">
                  {job.location}
                </span>
              </div>
            </div>

            <hr className="my-6 border-gray-200" />

            {/* Job Description */}
            <div className="mb-8">
              <h3 className="text-xl text-gray-900 mb-4">
                {t('publicJobPage.jobDescription')}
              </h3>
              <div className="prose prose-gray max-w-none text-gray-700 leading-relaxed">
                {job.description ? parse(job.description) : t('publicJobPage.noDescriptionAvailable')}
              </div>
            </div>

            {/* Required Skills */}
            {job.required_skills && job.required_skills.length > 0 && (
              <div className="mb-8">
                <h4 className="text-lg text-gray-900 mb-3">
                  {t('publicJobPage.requiredSkills')}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {job.required_skills.map((skill, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800 border border-blue-200"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Nice to Have Skills */}
            {job.nice_to_have_skills && job.nice_to_have_skills.length > 0 && (
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  {t('publicJobPage.niceToHave')}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {job.nice_to_have_skills.map((skill, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-700 border border-gray-200"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t('publicJobPage.jobDetails')}
            </h3>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">INR</div>
                <div>
                  <p className="text-gray-900">{t('publicJobPage.salary')}</p>
                  <p className="text-gray-600">
                    {formatSalary(job.salary_range_min, job.salary_range_max)}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <GraduationCap className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-gray-900">{t('publicJobPage.experienceLevel')}</p>
                  <p className="text-gray-600">
                    {formatExperienceLevel(job.experience_level)}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Briefcase className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-gray-900">{t('publicJobPage.employmentType')}</p>
                  <p className="text-gray-600">
                    {formatEmploymentType(job.employment_type)}
                  </p>
                </div>
              </div>

              {job.application_deadline && (
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Clock className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-gray-900">{t('publicJobPage.applicationDeadline')}</p>
                    <p className="text-gray-600">
                      {formatDate(job.application_deadline)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Apply Button */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('publicJobPage.interestedInPosition')}
            </h3>
            <p className="text-gray-600 mb-4">
              {t('publicJobPage.signUpToApply')}
            </p>
            {applicationStatus?.has_applied ? (
              <div>
                <button
                  disabled
                  className="w-full bg-green-100 text-green-800 px-6 py-3 rounded-xl flex items-center justify-center space-x-2 cursor-not-allowed"
                >
                  <CheckCircle className="h-5 w-5" />
                  <span>{t('publicJobPage.alreadyApplied')}</span>
                </button>
                <p className="text-center text-gray-500 mt-2">
                  {t('publicJobPage.status')}{" "}
                  {applicationStatus.application?.status_display ||
                    t('publicJobPage.pendingReview')}
                </p>
              </div>
            ) : (
              <div>
                <button
                  onClick={handleApplyClick}
                  disabled={applying || skillMatchLoading}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-xl transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {applying || skillMatchLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>
                        {applying ? t('publicJobPage.applying') : t('publicJobPage.analyzingSkills')}
                      </span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-5 w-5" />
                      <span>{t('publicJobPage.applyNow')}</span>
                    </>
                  )}
                </button>
                <p className="text-center text-gray-500 mt-2">
                  {t('publicJobPage.freeRegistrationRequired')}
                </p>
              </div>
            )}

            {/* Practice Interview Option */}
            <button
              onClick={handlePracticeClick}
              className="w-full mt-3 bg-white hover:bg-slate-50 text-indigo-600 border border-indigo-200 hover:border-indigo-300 font-bold px-6 py-3 rounded-xl transition-all duration-200 flex items-center justify-center space-x-2 shadow-sm"
            >
              <span>{t('publicJobPage.practiceInterview', 'Practice Interview')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <MetaTags
        title={pageTitle}
        description={pageDescription}
        url={pageUrl}
        keywords={pageKeywords}
        type="article"
      />

      {/* Conditional Layout Based on Authentication */}
      {isAuthenticated ? (
        // Authenticated users - with sidebar layout
        <ResponsiveLayout onLogout={handleLogout}>
          <JobDetailsContent />
        </ResponsiveLayout>
      ) : (
        // Non-authenticated users - simple navbar
        <>
          <div className="bg-white/80 backdrop-blur-lg border-b border-white/20 shadow-sm sticky top-0 z-40">
            <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between h-16">
                <div className="flex items-center space-x-4">
                  <img
                    src={logo}
                    alt="Logo"
                    width={100}
                    height={100}
                    style={{ borderRadius: "8px" }}
                  />
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => navigate("/login")}
                    className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all duration-200"
                  >
                    {t('publicJobPage.signIn')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="dashboard-container">
            <JobDetailsContent />
          </div>
        </>
      )}

      {/* All Dialogs - Remain the same */}

      {/* Skill Match Dialog */}
      {showSkillMatchDialog && (
        <div className="fixed top-16 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-blue-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {t('publicJobPage.skillMatchAnalysis')}
                  </h2>
                </div>
                <button
                  onClick={handleCancelApplication}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {skillMatchLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : skillMatchData ? (
                <div className="space-y-6">
                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {skillMatchData.job_title} at{" "}
                      {skillMatchData.company_name}
                    </h3>

                    <div className="bg-gray-50 rounded-xl p-6 mb-4">
                      <div className="text-4xl font-bold text-blue-600 mb-2">
                        {skillMatchData.skill_match?.match_percentage || 0}%
                      </div>
                      <div className="text-lg text-gray-700 mb-4">
                        {t('publicJobPage.skillMatch')}
                      </div>

                      {(skillMatchData.skill_match?.match_percentage || 0) <
                        30 ? (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <p className="text-yellow-800">
                            <strong>{t('publicJobPage.lowSkillMatch')}</strong> {t('publicJobPage.lowSkillMatchDesc')}
                          </p>
                        </div>
                      ) : (skillMatchData.skill_match?.match_percentage || 0) <
                        60 ? (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <p className="text-blue-800">
                            <strong>{t('publicJobPage.moderateMatch')}</strong> {t('publicJobPage.moderateMatchDesc')}
                          </p>
                        </div>
                      ) : (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <p className="text-green-800">
                            <strong>{t('publicJobPage.greatMatch')}</strong> {t('publicJobPage.greatMatchDesc')}
                          </p>
                        </div>
                      )}
                    </div>

                    {skillMatchData.skill_match?.details && (
                      <div className="space-y-4">
                        <h4 className="text-lg font-semibold text-gray-900">
                          {t('publicJobPage.skillBreakdown')}
                        </h4>

                        {skillMatchData.skill_match.details.matched_skills
                          ?.length > 0 && (
                            <div className="text-left">
                              <h5 className="font-semibold text-green-700 mb-2 flex items-center">
                                <CheckCircle className="h-4 w-4 mr-2" />✅ {t('publicJobPage.matchedSkills')} (
                                {
                                  skillMatchData.skill_match.details
                                    .matched_skills.length
                                }
                                )
                              </h5>
                              <div className="flex flex-wrap gap-2">
                                {skillMatchData.skill_match.details.matched_skills.map(
                                  (skill, index) => (
                                    <span
                                      key={index}
                                      className="px-3 py-1 bg-green-100 text-green-800 rounded-full border border-green-200"
                                    >
                                      {skill}
                                    </span>
                                  )
                                )}
                              </div>
                            </div>
                          )}

                        {skillMatchData.skill_match.details.missing_skills
                          ?.length > 0 && (
                            <div className="text-left">
                              <h5 className="font-semibold text-orange-700 mb-2 flex items-center">
                                <AlertCircle className="h-4 w-4 mr-2" />
                                ⚠️ {t('publicJobPage.missingSkills')} (
                                {
                                  skillMatchData.skill_match.details
                                    .missing_skills.length
                                }
                                )
                              </h5>
                              <div className="flex flex-wrap gap-2">
                                {skillMatchData.skill_match.details.missing_skills.map(
                                  (skill, index) => (
                                    <span
                                      key={index}
                                      className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full border border-orange-200"
                                    >
                                      {skill}
                                    </span>
                                  )
                                )}
                              </div>
                            </div>
                          )}

                        {skillMatchData.skill_match.details.extra_skills
                          ?.length > 0 && (
                            <div className="text-left">
                              <h5 className="font-semibold text-blue-700 mb-2 flex items-center">
                                <Star className="h-4 w-4 mr-2" />
                                💡 {t('publicJobPage.additionalSkills')} (
                                {
                                  skillMatchData.skill_match.details.extra_skills
                                    .length
                                }
                                )
                              </h5>
                              <div className="flex flex-wrap gap-2">
                                {skillMatchData.skill_match.details.extra_skills.map(
                                  (skill, index) => (
                                    <span
                                      key={index}
                                      className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full border border-blue-200"
                                    >
                                      {skill}
                                    </span>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                    )}

                    {skillMatchData.skill_match?.summary && (
                      <div className="bg-blue-50 rounded-lg p-4">
                        <h5 className="font-semibold text-blue-900 mb-2">
                          {t('publicJobPage.analysisSummary')}
                        </h5>
                        <p className="text-blue-800">
                          {skillMatchData.skill_match.summary}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-gray-600 text-center py-4">
                  {t('publicJobPage.unableToAnalyze')}
                </p>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={handleCancelApplication}
                className="px-6 py-2 text-gray-700 hover:text-gray-900 transition-colors"
              >
                {t('publicJobPage.cancel')}
              </button>
              <button
                onClick={handleProceedWithApplication}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all duration-200 flex items-center space-x-2"
              >
                <CheckCircle className="h-4 w-4" />
                <span>
                  {(skillMatchData?.skill_match?.match_percentage || 0) < 30
                    ? t('publicJobPage.applyAnyway')
                    : t('publicJobPage.proceedWithApplication')}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Interview Prompt Dialog */}
      {showInterviewPrompt && (
        <div className="fixed top-16 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {t('publicJobPage.applicationSubmitted')}
                </h2>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <p className="text-gray-700 mb-4">
                  <Trans
                    i18nKey="publicJobPage.applicationSubmittedDesc"
                    values={{
                      title: jobData?.job?.title,
                      company: jobData?.job?.company_name,
                    }}
                    components={{ strong: <strong /> }}
                  />
                </p>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-blue-800">
                    <strong>{t('publicJobPage.enhanceApplication')}</strong> {t('publicJobPage.enhanceApplicationDesc')}
                  </p>
                </div>

                <p className="text-gray-600">
                  {t('publicJobPage.interviewBenefit')}
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => handleInterviewPromptResponse(false)}
                disabled={interviewPromptLoading}
                className="px-6 py-2 text-gray-700 hover:text-gray-900 transition-colors disabled:opacity-50"
              >
                {t('publicJobPage.noThanks')}
              </button>
              <button
                onClick={() => handleInterviewPromptResponse(true)}
                disabled={interviewPromptLoading}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all duration-200 flex items-center space-x-2 disabled:opacity-50"
              >
                {interviewPromptLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{t('publicJobPage.processing')}</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    <span>{t('publicJobPage.yesTakeInterview')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmDialogOpen && (
        <div className="fixed top-16 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-[9999] min-h-screen sm:min-h-80">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-blue-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {t('publicJobPage.confirmJobApplication')}
                </h2>
              </div>
            </div>

            <div className="p-4 sm:p-6 space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {jobData?.job?.title}
                </h3>
                <p className="text-gray-600">{jobData?.job?.company_name}</p>
              </div>

              <div>
                <p className="text-gray-700 mb-4">
                  {t('publicJobPage.confirmApplyQuestion')}
                </p>
              </div>

              <div>
                <label className="block text-gray-700 mb-2">
                  {t('publicJobPage.coverLetterOptional')}
                </label>
                <textarea
                  value={coverLetterInput}
                  onChange={(e) => setCoverLetterInput(e.target.value)}
                  rows={3}
                  placeholder={t('publicJobPage.coverLetterPlaceholder')}
                  className="w-full border border-gray-300 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg p-3 text-sm sm:text-base text-gray-900 placeholder-gray-400 outline-none"
                />
                <p className="mt-2 text-gray-500">
                  {t('publicJobPage.coverLetterDefault')}
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">
                  {t('publicJobPage.whatWillBeShared')}
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <div>
                      <p className="text-gray-900">{t('publicJobPage.yourResumeCV')}</p>
                      <p className="text-gray-600">
                        {t('publicJobPage.resumeSharedWithCompany')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <div>
                      <p className="text-gray-900">{t('publicJobPage.contactInformation')}</p>
                      <p className="text-gray-600">
                        {t('publicJobPage.contactInfoShared')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <div>
                      <p className="text-gray-900">{t('publicJobPage.coverLetter')}</p>
                      <p className="text-gray-600">
                        {t('publicJobPage.coverLetterBriefIntro')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">
                  {t('publicJobPage.forCompanyAdmins')}
                </h4>
                <p className="text-blue-800">
                  {t('publicJobPage.companyAdminsInfo')}
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800">
                  {t('publicJobPage.uploadResumeNote')}
                </p>
              </div>
            </div>

            <div className="p-4 sm:p-6 border-t border-gray-200 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={() => setConfirmDialogOpen(false)}
                disabled={applying}
                className="px-6 py-2 text-gray-700 hover:text-gray-900 transition-colors disabled:opacity-50"
              >
                {t('publicJobPage.cancel')}
              </button>
              <button
                onClick={handleConfirmApply}
                disabled={applying}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all duration-200 flex items-center space-x-2 disabled:opacity-50"
              >
                {applying ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{t('publicJobPage.submittingApplication')}</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    <span>{t('publicJobPage.yesSubmitApplication')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resume Selection Dialog */}
      {showResumeSelectDialog && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[9999] fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-slideUp">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 tracking-wide">
                {t('publicJobPage.selectAResume')}
              </h2>
            </div>

            <div className="p-6 space-y-3 max-h-80 overflow-y-auto">
              {resumesLoading ? (
                <p className="text-gray-500 text-center text-sm py-4">{t('publicJobPage.loadingResumes')}</p>
              ) : userResumes.length > 0 ? (
                <div className="space-y-3">
                  {userResumes.map((resume) => (
                    <button
                      key={resume.id}
                      onClick={() => setSelectedResume(resume.id)}
                      className={`w-full text-left border rounded-xl p-4 transition-all hover:shadow-md hover:border-blue-400 ${selectedResume === resume.id
                        ? "border-blue-600 shadow-lg bg-blue-50"
                        : "border-gray-300 bg-white"
                        }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-gray-900">
                          {resume.original_filename}
                        </span>

                        {resume.is_active && (
                          <span className="text-xs px-2 py-1 rounded-md bg-blue-100 text-blue-600 font-semibold">
                            {t('publicJobPage.active')}
                          </span>
                        )}
                      </div>

                      <p className="text-xs text-gray-500 mt-1">
                        {t('publicJobPage.uploadedOn', { date: formatDate(resume.uploaded_at) })}
                      </p>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center text-sm bg-gray-50 p-4 rounded-lg">
                  {t('publicJobPage.noResumesFound')}
                </p>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowResumeSelectDialog(false)}
                className="px-5 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-150"
              >
                {t('publicJobPage.cancel')}
              </button>

              <button
                onClick={handleResumeSelectionAndPreview}
                disabled={!selectedResume || skillMatchLoading}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-lg hover:shadow-xl transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {skillMatchLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{t('publicJobPage.analyzing')}</span>
                  </>
                ) : (
                  <span>{t('publicJobPage.continue')}</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar */}
      {snackbar.open && (
        <div className="fixed top-4 right-4 z-[9999] w-full max-w-sm sm:max-w-md md:max-w-lg px-4">
          <div
            className={`p-4 rounded-xl shadow-lg border break-words whitespace-normal
        ${snackbar.severity === "success"
                ? "bg-green-50 border-green-200 text-green-800"
                : snackbar.severity === "error"
                  ? "bg-red-50 border-red-200 text-red-800"
                  : snackbar.severity === "warning"
                    ? "bg-yellow-50 border-yellow-200 text-yellow-800"
                    : "bg-blue-50 border-blue-200 text-blue-800"
              }`}
          >
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                {snackbar.severity === "success" && (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                )}
                {snackbar.severity === "error" && (
                  <AlertCircle className="h-5 w-5 text-red-600" />
                )}
                {snackbar.severity === "warning" && (
                  <AlertCircle className="h-5 w-5 text-yellow-600" />
                )}
                {snackbar.severity === "info" && (
                  <AlertCircle className="h-5 w-5 text-blue-600" />
                )}
              </div>
              <div className="flex-1 overflow-hidden">
                <p className="text-sm break-words">{snackbar.message}</p>
              </div>
              <button
                onClick={handleCloseSnackbar}
                className="flex-shrink-0 p-1 hover:bg-black hover:bg-opacity-10 rounded transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resume Upload Dialog */}
      {showUploadDialog && (
        <div className="fixed top-16 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Upload className="h-6 w-6 text-blue-600" />
                  </div>
                  <h2 className="text-xl text-gray-900">
                    {t('publicJobPage.uploadResumeRequired')}
                  </h2>
                </div>
                <button
                  onClick={() => setShowUploadDialog(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-blue-800">
                    {t('publicJobPage.uploadResumeBeforeApply')}
                  </p>
                </div>

                <div className="flex items-center justify-center">
                  <label
                    className={`bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer flex items-center space-x-2 ${uploadLoading ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                  >
                    <Upload className="w-5 h-5" />
                    <span>
                      {uploadLoading ? t('publicJobPage.uploading') : t('publicJobPage.chooseResumeFile')}
                    </span>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleResumeUpload}
                      className="hidden"
                      disabled={uploadLoading}
                    />
                  </label>
                </div>

                <p className="text-gray-500 text-center mt-2">
                  {t('publicJobPage.supportedFormats')}
                </p>
              </div>

              {resumesLoading ? (
                <div className="text-center py-4">
                  <RefreshCw className="w-6 h-6 animate-spin text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">{t('publicJobPage.loadingExistingResumes')}</p>
                </div>
              ) : userResumes.length > 0 ? (
                <div>
                  <h4 className="text-gray-900 mb-3">
                    {t('publicJobPage.yourExistingResumes')}
                  </h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {userResumes.map((resume) => (
                      <div
                        key={resume.id}
                        className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                          <Upload className="w-4 h-4 text-red-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-gray-900">
                            {resume.original_filename ||
                              resume.name ||
                              t('publicJobPage.uploadedResume')}
                          </p>
                          <p className="text-gray-500">
                            {t('publicJobPage.uploaded', { date: formatDate(resume.uploaded_at) })}
                          </p>
                        </div>
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-800">
                      {t('publicJobPage.resumeCount', { count: userResumes.length })}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-gray-500">
                    {t('publicJobPage.noResumesUpload')}
                  </p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowUploadDialog(false)}
                className="px-6 py-2 text-gray-700 hover:text-gray-900 transition-colors"
              >
                {t('publicJobPage.cancel')}
              </button>
              {userResumes.length > 0 && (
                <button
                  onClick={() => {
                    setShowUploadDialog(false);
                    handleApplyClick();
                  }}
                  className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all duration-200 flex items-center space-x-2"
                >
                  <CheckCircle className="h-4 w-4" />
                  <span>{t('publicJobPage.continueApplication')}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <B2CInterviewSetupModal
        isOpen={showPracticeModal}
        onClose={() => setShowPracticeModal(false)}
        resume={selectedResumeData}
        onStartInterview={(sessionId, inviteToken) => {
          let baseInterviewUrl = process.env.REACT_APP_INTERVIEW_UI_URL || window.location.origin;
          const relativePath = inviteToken ? `/interview/invite/${inviteToken}` : `/interview/${sessionId}?startInterview=true`;
          window.open(`${baseInterviewUrl}${relativePath}`, '_blank');
        }}
        creditBalance={credits}
        initialSkills={job.required_skills || []}
        initialExperienceLevel={job.experience_level}
        jobDescription={stripHtml(job.description)}
        targetRole={job.title}
      />
    </>
  );
}
